"use client";

import { memo } from "react";
import { calculateDaysAgo, formatReleaseDays } from "@/lib/app-detail-utils";
import { useThemeConfig } from "@/hooks/use-theme-config";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface AppWhatsNewProps {
  releaseNotes: string | null;
  version: string | null;
  currentVersionReleaseDate: string | null;
  isLoading: boolean;
}

export const AppWhatsNew = memo(function AppWhatsNew({
  releaseNotes,
  version,
  currentVersionReleaseDate,
  isLoading = false,
}: AppWhatsNewProps) {
  const { isTerminal, hasGlow, mounted } = useThemeConfig();

  const showTerminal = mounted && isTerminal;

  const daysAgo = mounted ? calculateDaysAgo(currentVersionReleaseDate) : null;
  const formattedDate = daysAgo !== null ? formatReleaseDays(daysAgo) : null;

  // Format release date for terminal display
  const terminalDate = currentVersionReleaseDate
    ? new Date(currentVersionReleaseDate).toISOString().split("T")[0].replace(/-/g, ".")
    : null;

  // Terminal mode section header
  const terminalHeader = showTerminal ? (
    <div className="flex items-center gap-2 mb-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 [text-shadow:0_0_10px_rgba(52,211,153,0.8)]">
        [UPD.LOG]
      </span>
      <div className="flex-1 h-px bg-gradient-to-r from-emerald-500/50 to-transparent" />
      <span className="font-mono text-[8px] uppercase tracking-wider text-emerald-600 animate-pulse">
        CHANGELOG
      </span>
    </div>
  ) : null;

  // Terminal skeleton
  const terminalSkeleton = (
    <LazyMotion features={domAnimation}>
      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={cn(
          "bg-black border-2 border-emerald-700/50 p-4 rounded-none",
          "shadow-[0_0_15px_rgba(16,185,129,0.15),inset_0_0_20px_rgba(16,185,129,0.05)]"
        )}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-2">
            <Skeleton className="h-4 w-20 bg-emerald-950/50 border border-emerald-800/30" />
            <Skeleton className="h-3 w-24 bg-emerald-950/50 border border-emerald-800/30" />
          </div>
          <Skeleton className="h-4 w-full bg-emerald-950/50 border border-emerald-800/30" />
          <Skeleton className="h-4 w-11/12 bg-emerald-950/50 border border-emerald-800/30" />
          <Skeleton className="h-4 w-4/5 bg-emerald-950/50 border border-emerald-800/30" />
        </div>
      </m.div>
    </LazyMotion>
  );

  if (isLoading) {
    if (showTerminal) {
      return (
        <div className="h-full">
          {terminalHeader}
          {terminalSkeleton}
        </div>
      );
    }

    return (
      <div className="h-full">
        <h2 className="text-xl font-bold tracking-tight mb-3">What&apos;s New</h2>
        <div className="space-y-3">
          <div className="h-4 w-24 bg-muted/50 rounded animate-pulse" />
          <div className="h-4 w-full bg-muted/50 rounded animate-pulse" />
          <div className="h-4 w-3/4 bg-muted/50 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (!releaseNotes) {
    if (showTerminal) {
      return (
        <div className="h-full">
          {terminalHeader}
          <LazyMotion features={domAnimation}>
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={cn(
                "bg-black border-2 border-emerald-700/50 p-4 rounded-none",
                "shadow-[0_0_15px_rgba(16,185,129,0.15)]"
              )}
            >
              <div className="font-mono text-sm text-emerald-600 text-center">
                <span className="text-emerald-500">[</span>
                NO_CHANGELOG_DATA
                <span className="text-emerald-500">]</span>
              </div>
            </m.div>
          </LazyMotion>
        </div>
      );
    }
    return null;
  }

  // Terminal mode rendering
  if (showTerminal) {
    return (
      <div className="h-full flex flex-col">
        {terminalHeader}
        <LazyMotion features={domAnimation}>
        <m.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className={cn(
            "flex-1 bg-black border-2 border-emerald-700/50 p-4 rounded-none relative overflow-hidden",
            "shadow-[0_0_15px_rgba(16,185,129,0.15),inset_0_0_20px_rgba(16,185,129,0.05)]",
            hasGlow && "animate-terminal-border-pulse"
          )}
        >
          {/* Scanline overlay */}
          <div className="absolute inset-0 pointer-events-none opacity-10 bg-[repeating-linear-gradient(0deg,transparent,transparent_2px,rgba(16,185,129,0.1)_2px,rgba(16,185,129,0.1)_4px)]" />

          {/* Corner accents */}
          <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-emerald-400/60" />
          <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-emerald-400/60" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-emerald-400/60" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-emerald-400/60" />

          {/* Status indicator */}
          <div className="absolute top-2 right-2 flex items-center gap-1">
            <div className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_4px_rgba(52,211,153,0.8)]" />
            <span className="font-mono text-[8px] uppercase tracking-wider text-emerald-600">LIVE</span>
          </div>

          <div className="relative z-10">
            {/* Version and date header */}
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-emerald-700/30">
              {version && (
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[9px] uppercase tracking-wider text-emerald-600">VER:</span>
                  <span className={cn(
                    "font-mono text-sm font-bold text-emerald-300",
                    "[text-shadow:0_0_8px_rgba(52,211,153,0.6),0_0_16px_rgba(52,211,153,0.4)]"
                  )}>
                    {version}
                  </span>
                </div>
              )}
              {terminalDate && (
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[9px] uppercase tracking-wider text-emerald-600">REL:</span>
                  <span className="font-mono text-xs text-emerald-400/80">
                    {terminalDate}
                  </span>
                  {formattedDate && (
                    <span className="font-mono text-[10px] text-emerald-500/60">
                      ({formattedDate})
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Release notes content */}
            <div className={cn(
              "font-mono text-[12px] text-emerald-300/90 leading-[1.7] overflow-y-auto max-h-[180px]",
              "[text-shadow:0_0_4px_rgba(52,211,153,0.3)]",
              // Custom scrollbar for terminal
              "[&::-webkit-scrollbar]:w-1.5",
              "[&::-webkit-scrollbar-track]:bg-emerald-950/50",
              "[&::-webkit-scrollbar-thumb]:bg-emerald-600/50",
              "[&::-webkit-scrollbar-thumb]:rounded-full",
              "[&::-webkit-scrollbar-thumb:hover]:bg-emerald-500/70"
            )}>
              {/* Terminal command prefix */}
              <div className="mb-2">
                <span className="text-emerald-500 select-none">$ cat CHANGELOG.md</span>
              </div>
              <div className="text-emerald-600 select-none mb-1">---</div>
              <div className="whitespace-pre-line pl-2 border-l border-emerald-700/40">
                {releaseNotes}
              </div>
              <div className="text-emerald-600 select-none mt-1">---</div>
              <div className="mt-2 flex items-center">
                <span className="text-emerald-500 select-none">$ </span>
                <span className="text-emerald-400 ml-1">
                  _
                </span>
              </div>
            </div>
          </div>
        </m.div>
        </LazyMotion>
      </div>
    );
  }

  // Default light/dark mode rendering
  return (
    <div className="h-full flex flex-col">
      <h2 className="text-xl font-bold tracking-tight mb-3">What&apos;s New</h2>
      <div className="flex items-baseline justify-between mb-3">
        {version && (
          <span className="text-base font-semibold text-foreground">
            Version {version}
          </span>
        )}
        {formattedDate && (
          <span className="text-sm text-muted-foreground">
            {formattedDate}
          </span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto max-h-[200px] text-[15px] text-muted-foreground leading-[1.6] whitespace-pre-line">
        {releaseNotes}
      </div>
    </div>
  );
});
